/**
 * arSession — the editor side of the AR live bridge.
 *
 * On Run (when an AR session is open), the editor converts the current sketch and
 * publishes it to `arSessions/{sessionId}`; the mobile viewer subscribes and re-runs
 * on each runId bump. The sessionId is an unguessable, per-project token stored on the
 * project doc so the same QR works across reloads.
 *
 * The global→instance conversion happens HERE (client-side), never in a Cloud
 * Function or on the phone.
 */

import {
  doc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db, auth } from './init';
import { buildArSessionPayload } from '../utils/arSessionPayload';
import { AR_SETTINGS_DEFAULTS } from '../../common/types/arSettings';
import {
  readStaffArSessionId,
  rememberStaffArSessionId
} from '../utils/staffArSession';

/** Unguessable, URL-clean session token (128 bits of randomness), no deps. */
export function generateSessionId() {
  const c = (typeof window !== 'undefined' && window.crypto) || undefined;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID().replace(/-/g, '');
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Return the AR session id to publish into, creating one the first time.
 * Requires a saved project (has an id).
 *
 * Two cases, because a session document is owner-locked:
 *   - **Owner** — the project's own `arSessionId`, persisted onto the project doc so the
 *     same QR works across reloads and for anyone who reopens the sketch.
 *   - **Instructor viewing a student's sketch** (`isOwner: false`) — a SEPARATE session of
 *     their own, kept in `localStorage` (see `utils/staffArSession`). The student's session
 *     doc pins `ownerId` write-once, so publishing into it would be PERMISSION_DENIED; and
 *     overwriting `projects/{id}.arSessionId` (which staff rules would permit) would orphan
 *     the student's QR, their stored `sliderValues`, and the `arSettings` a classroom
 *     publication inherits from that doc. So this branch touches neither.
 *
 * @param {{ id?: string, arSessionId?: string }} project the Redux project state
 * @param {{ isOwner?: boolean }} [options]
 * @returns {Promise<string>}
 */
export async function ensureArSessionId(project, options = {}) {
  if (!project || !project.id) {
    throw new Error('Save your sketch before opening it in AR.');
  }

  const { isOwner = true } = options;
  if (!isOwner) {
    const uid = auth.currentUser && auth.currentUser.uid;
    if (!uid) throw new Error('You must be signed in to publish to AR.');
    const existing = readStaffArSessionId(project.id, uid);
    if (existing) return existing;
    const staffSessionId = generateSessionId();
    rememberStaffArSessionId(project.id, uid, staffSessionId);
    return staffSessionId;
  }

  if (project.arSessionId) return project.arSessionId;

  const sessionId = generateSessionId();
  // Owner-only patch; userId/ownerUsername/createdAt are untouched so the project
  // update rule passes. (Does not bump updatedAt — AR is decoupled from autosave.)
  await updateDoc(doc(db, 'projects', project.id), { arSessionId: sessionId });
  return sessionId;
}

/**
 * Convert the current sketch and publish it to the session doc. Bumps runId
 * atomically so the viewer re-runs. Must be called by the authenticated owner.
 *
 * @param {object} args
 * @param {string} args.sessionId
 * @param {Array} args.files the editor's live files (state.files)
 * @param {object} [args.arSettings] current AR alignment settings (mirrored so a freshly
 *   opened viewer starts correct); omitted when unavailable.
 * @returns {Promise<{ usesDegrees: boolean }>} editor-only facts about the sketch that was
 *   just published. `usesDegrees` drives the "use radians" warning bar; it is NOT written to
 *   the session doc — the setDoc below lists its fields explicitly, so nothing leaks in.
 */
export async function publishArSession({ sessionId, files, arSettings }) {
  const uid = auth.currentUser && auth.currentUser.uid;
  if (!uid) throw new Error('You must be signed in to publish to AR.');

  // Reuse the tested builder for code + metadata + ownerId. runId is owned by the
  // write below (atomic increment), so the builder's runId value is intentionally
  // discarded.
  const stripOrbitControl = arSettings
    ? arSettings.stripOrbitControl
    : AR_SETTINGS_DEFAULTS.stripOrbitControl;
  const stripBackground = arSettings
    ? arSettings.stripBackground
    : AR_SETTINGS_DEFAULTS.stripBackground;
  const {
    code,
    width,
    height,
    webgl,
    p5Version,
    helperScripts,
    usesDegrees
  } = buildArSessionPayload({
    files,
    ownerId: uid,
    prevRunId: 0,
    stripOrbitControl,
    stripBackground
  });

  // `merge: true` intentionally does NOT clear `sliderValues`: slider state persists by
  // design across Runs/reopens (persist-by-default), with the Firestore doc as the
  // source of truth for the persisted value. A sketch opts out via `resetArSliders()`
  // (client-side), not by this write clearing the field.
  await setDoc(
    doc(db, 'arSessions', sessionId),
    {
      code,
      width,
      height,
      webgl,
      // The p5 version the viewer must load so the converted `p.<fn>()` calls resolve.
      // Written on every Run (alongside the runId bump) — a version change in the editor
      // reaches the phone on the next Run, which reloads the viewer to swap p5.
      p5Version,
      // Non-sketch JS the viewer must eval before running (constellation data +
      // skyTransforms.js / skyDraw.js). Dropping this leaves the phone with e.g.
      // `createStarGeometry is not defined`. Always an array (possibly empty) — safe for Firestore.
      helperScripts,
      ownerId: uid,
      runId: increment(1),
      updatedAt: serverTimestamp(),
      ...(arSettings ? { arSettings } : {})
    },
    { merge: true }
  );

  return { usesDegrees };
}

/**
 * Push AR settings to an existing session WITHOUT bumping runId, so the viewer applies them
 * live (today that means showGuiInAr) without re-running the sketch. The session doc must
 * already exist (created by publishArSession).
 *
 * @param {object} args
 * @param {string} args.sessionId
 * @param {import('../../common/types/arSettings').ARSettings} args.arSettings
 * @returns {Promise<void>}
 */
export async function publishArSettings({ sessionId, arSettings }) {
  const uid = auth.currentUser && auth.currentUser.uid;
  if (!uid) throw new Error('You must be signed in to publish to AR.');

  await updateDoc(doc(db, 'arSessions', sessionId), { arSettings });
}

/**
 * Push live slider values to an existing session as nested field paths
 * (`sliderValues.<name>`), WITHOUT bumping runId or touching updatedAt — the viewer
 * applies these to the running sketch instead of re-running it. No-ops on a falsy
 * sessionId or an empty patch.
 *
 * @param {string} sessionId
 * @param {Record<string, unknown>} patch slider name -> value
 * @returns {Promise<void>}
 */
export async function publishSliderValues(sessionId, patch) {
  if (!sessionId || !patch) return;
  const entries = Object.entries(patch);
  if (entries.length === 0) return;
  const update = {};
  entries.forEach(([name, value]) => {
    update[`sliderValues.${name}`] = value;
  });
  await updateDoc(doc(db, 'arSessions', sessionId), update);
}

/**
 * Subscribe to the live session doc (owner side). `onData` is called with the doc data on
 * the first snapshot and every subsequent change — used to forward remote `sliderValues`
 * (nudged from the AR viewer) back down to the editor's preview. Mirrors `subscribeProject`
 * (client/firebase/projects.ts) and the AR viewer's own `subscribeSession`. Returns the
 * unsubscribe function.
 *
 * @param {string} sessionId
 * @param {(data: object) => void} onData
 * @param {(err: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export function subscribeArSession(sessionId, onData, onError) {
  return onSnapshot(
    doc(db, 'arSessions', sessionId),
    (snap) => {
      if (snap.exists()) onData(snap.data());
    },
    (err) => {
      if (onError) onError(err);
    }
  );
}
